import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { Starfield } from "../components/Starfield";
import { DiamondMark } from "../components/DiamondMark";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          await checkAuth();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current > 12) {
        setStatus("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, checkAuth]);

  return (
    <div className="relative min-h-screen bg-cg-bg flex items-center justify-center px-5">
      <Starfield count={60} />
      <div className="relative z-10 w-full max-w-md rounded-3xl cg-glass p-10 text-center">
        {status === "checking" && (
          <>
            <div className="animate-floaty mx-auto w-fit mb-6"><DiamondMark size={54} /></div>
            <p className="text-cg-texts font-mono uppercase tracking-widest text-sm">Confirming your ascension…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-cg-cyan mx-auto mb-5" />
            <h1 className="font-serif text-2xl text-cg-textp mb-2">You've ascended</h1>
            <p className="text-sm text-cg-texts mb-7">Your plan is active. Every portal is now open to you.</p>
            <Button data-testid="success-continue-button" onClick={() => navigate("/app")} className="rounded-full bg-cg-violet hover:bg-cg-violetglow text-white px-8">
              Enter your sessions
            </Button>
          </>
        )}
        {(status === "pending" || status === "error") && (
          <>
            <XCircle className="h-14 w-14 text-cg-gold mx-auto mb-5" />
            <h1 className="font-serif text-2xl text-cg-textp mb-2">
              {status === "pending" ? "Still processing" : "Something interrupted"}
            </h1>
            <p className="text-sm text-cg-texts mb-7">
              {status === "pending"
                ? "Your payment is taking a moment. Check your account shortly."
                : "We couldn't confirm the payment. Please try again."}
            </p>
            <Button onClick={() => navigate("/pricing")} className="rounded-full bg-cg-violet hover:bg-cg-violetglow text-white px-8">
              Back to pricing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="relative min-h-screen bg-cg-bg flex items-center justify-center px-5">
      <Starfield count={50} />
      <div className="relative z-10 w-full max-w-md rounded-3xl cg-glass p-10 text-center">
        <XCircle className="h-14 w-14 text-cg-gold mx-auto mb-5" />
        <h1 className="font-serif text-2xl text-cg-textp mb-2">Checkout cancelled</h1>
        <p className="text-sm text-cg-texts mb-7">No charge was made. Your path is still open whenever you're ready.</p>
        <Button data-testid="cancel-back-button" onClick={() => navigate("/pricing")} className="rounded-full bg-cg-violet hover:bg-cg-violetglow text-white px-8">
          Back to pricing
        </Button>
      </div>
    </div>
  );
}
